package io.peekandpoke.kraft.forms

import io.peekandpoke.kraft.components.Component
import io.peekandpoke.kraft.components.Ctx
import io.peekandpoke.kraft.i18n.i18nCtrl
import io.peekandpoke.kraft.messages.sendMessage
import io.peekandpoke.kraft.utils.launch
import io.peekandpoke.ultra.html.onClick
import io.peekandpoke.ultra.i18n.I18nTranslate
import kotlinx.html.FlowContent
import kotlinx.html.label
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.MouseEvent

/**
 * Base class for form field components that use [FieldOptions] for configuration and validation.
 *
 * Handles mount/unmount lifecycle, value tracking, validation, and label rendering.
 */
abstract class AbstractFormField<T, O : FieldOptions<T>, P : AbstractFormField.Props<T, O>>(
    ctx: Ctx<P>,
) : Component<P>(ctx), FormField<T> {

    //  PROPS  //////////////////////////////////////////////////////////////////////////////////////////////////

    /** Props for an [AbstractFormField], providing the current value, change callback, and options. */
    interface Props<T, O : FieldOptions<T>> {
        val value: T
        val onChange: (T) -> Unit
        val options: O
    }

    /** The field options controlling label, placeholder, validation rules, etc. */
    val options: O get() = props.options

    //  STATE  //////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Track if the input value was modified.
     *
     * 'True' means the value was modified and thus the field was touched.
     * 'False' means the value was not modified yet.
     */
    override var touched: Boolean by value(false)

    /**
     * A list of validation errors.
     */
    override var errors: List<String> by value(emptyList())

    /**
     * The input value set by the user.
     */
    private var _value: T by value(props.value)

    /** Current translations; re-validates on language switch so error messages update. */
    protected val translate: I18nTranslate by subscribingTo(i18nCtrl.translateStream) {
        if (touched) launch { validate() }
    }

    /**
     * The effective value
     *
     * - it is either a value set by the user (see [setValue])
     * - or the initial value coming through the props (see [Props.value])
     */
    val currentValue: T get() = _value

    //  IMPL  ///////////////////////////////////////////////////////////////////////////////////////////////////

    init {
        lifecycle {
            onMount {
                sendMessage(FormFieldMountedMessage(this@AbstractFormField))
            }

            onUnmount {
                sendMessage(FormFieldUnmountedMessage(this@AbstractFormField))
            }

            onNextProps { new, _ ->
                _value = new.value
            }
        }
    }

    override fun shouldRedraw(nextProps: P): Boolean {
        return props.value != nextProps.value ||
                props.options.attributes.entries.size != nextProps.options.attributes.entries.size ||
                // TODO: improve this as this is not very efficient
                props.options.attributes.asImmutable() != nextProps.options.attributes.asImmutable()
    }

    override fun reset() {
        touched = false
        _value = props.value
        errors = emptyList()
    }

    override fun touch() {
        touched = true
    }

    override fun untouch() {
        touched = false
    }

    /** Monotonic token so out-of-order async validations (later keystroke) win over stale ones. */
    private var validationSeq = 0

    override suspend fun validate(): Boolean {

        if (!touched) {
            return errors.isEmpty()
        }

        val seq = ++validationSeq
        // Snapshot the value/translation once — async rules suspend, and currentValue may change
        // mid-flight; re-reading it would compute errors against a mix of values.
        val value = currentValue
        val t = translate

        val newErrors = props.options.rules
            .filter { !it.check(value) }
            .map { it.getMessage(value, t) }

        // Latest-wins: only publish if a newer validation hasn't started while we were suspended.
        if (seq == validationSeq) {
            errors = newErrors
        }

        return newErrors.isEmpty()
    }

    /** Focuses the DOM element matching the given [cssSelector] within this component. */
    fun focus(cssSelector: String) {
        (dom?.querySelector(cssSelector) as? HTMLElement)?.focus()
    }

    /** Sets a new value, notifies the form controller, and re-validates. */
    fun setValue(value: T) {
//        console.log("setValue", value, (value as Any)::class)

        if (this._value != value) {
            try {
                sendMessage(FormFieldInputChanged(this))
            } finally {
            }

            touch()
            this._value = value
            props.onChange(value)
        }

        launch { validate() }
    }

    // Label Helpers

    /** Renders the field label; optionally focuses the element at [focusCssSelector] on click. */
    fun FlowContent.renderLabel(focusCssSelector: String? = null) {

        renderLabel(
            onClick = focusCssSelector?.let { sel ->
                { focus(sel) }
            }
        )
    }

    /** Renders the field label with an optional [onClick] handler. */
    fun FlowContent.renderLabel(onClick: ((evt: MouseEvent) -> Unit)? = null) {
        options.label()?.let { l ->
            label {
                l()

                onClick?.let { on ->
                    onClick { evt ->
                        on(evt)
                    }
                }
            }
        }
    }
}
